import { useQuery } from "@tanstack/react-query";
import type { RegimeState, RegimeQuadrant } from "@macroscope/shared";
import { fetchRegime } from "../lib/api";

export default function Regime() {
  const { data, isLoading } = useQuery({ queryKey: ["regime"], queryFn: fetchRegime });

  if (isLoading) return <div className="text-sm text-slate-500">Loading Regime Clock…</div>;

  const regimes = data?.regimes ?? [];
  const us = regimes.find((r) => r.country === "US");

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="space-y-2">
        <h1 className="text-lg font-semibold">Regime Clock</h1>
        <p className="max-w-2xl text-xs text-slate-500">
          Growth vs. inflation direction relative to trend — and the historical asset playbook for each quadrant. Educational, not a prediction.
        </p>
      </section>

      {us ? <RegimeDetail state={us} /> : <div className="text-sm text-slate-500">No regime data available.</div>}

      {data?.coverage_gaps && data.coverage_gaps.length > 0 && (
        <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4 text-xs text-yellow-500">
          {data.coverage_gaps.join(" · ")}
        </div>
      )}

      <div className="text-[10px] text-slate-600">
        Source: {data?.source} · {data?.updated_at ? new Date(data.updated_at).toLocaleString() : ""}
        {data?.delayed && " · delayed"} · educational
      </div>
    </div>
  );
}

function RegimeDetail({ state }: { state: RegimeState }) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
        <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">Macro regime quadrant</h2>
        <RegimeQuadrantClock state={state} />
      </div>

      <div className="space-y-6">
        <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
          <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">Current read</h2>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Regime</span>
              <span className="rounded bg-macro-accent/10 px-2 py-0.5 text-sm font-medium text-macro-accent capitalize">
                {state.quadrant}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Growth nowcast</span>
              <span className={`font-mono text-sm font-medium ${state.nowcast >= 0 ? "text-macro-up" : "text-macro-down"}`}>
                {state.nowcast >= 0 ? "+" : ""}
                {state.nowcast.toFixed(2)}σ
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Confidence</span>
              <span className="font-mono text-sm">{(state.confidence * 100).toFixed(0)}%</span>
            </div>
            <div className="rounded-lg border border-surface-2 bg-surface-2 p-3 text-sm text-slate-300">
              <span className="text-slate-500">Playbook: </span>
              {state.playbook}
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-surface-2 bg-surface-1 p-5">
          <h2 className="mb-4 text-xs font-medium uppercase tracking-wider text-slate-400">Driver z-scores</h2>
          <DriverList drivers={state.drivers} />
        </div>
      </div>
    </div>
  );
}

function RegimeQuadrantClock({ state }: { state: RegimeState }) {
  const quadrants: { key: RegimeQuadrant; label: string; position: string }[] = [
    { key: "reflation", label: "Reflation", position: "col-start-1 row-start-2" },
    { key: "recovery", label: "Recovery", position: "col-start-2 row-start-2" },
    { key: "stagflation", label: "Stagflation", position: "col-start-1 row-start-1" },
    { key: "overheat", label: "Overheat", position: "col-start-2 row-start-1" },
  ];

  return (
    <div className="relative">
      <div className="grid grid-cols-2 gap-2">
        {quadrants.map((q) => {
          const active = state.quadrant === q.key;
          return (
            <div
              key={q.key}
              className={`${q.position} flex h-28 flex-col items-center justify-center rounded-lg border text-center transition-colors ${
                active
                  ? "border-macro-accent bg-macro-accent/10 text-macro-accent"
                  : "border-surface-2 bg-surface-2 text-slate-500"
              }`}
            >
              <div className="text-xs font-medium uppercase tracking-wider">{q.label}</div>
              {active && <div className="mt-1 text-[10px] opacity-80">current</div>}
            </div>
          );
        })}
      </div>
      <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-surface-2" />
      <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-surface-2" />
      <div className="mt-2 flex justify-between text-[10px] text-slate-500">
        <span>growth ↓</span>
        <span>inflation →</span>
      </div>
    </div>
  );
}

function DriverList({ drivers }: { drivers: Record<string, number> }) {
  const items = Object.entries(drivers).sort(([, a], [, b]) => Math.abs(b) - Math.abs(a));
  if (items.length === 0) {
    return <div className="text-sm text-slate-500">No driver data.</div>;
  }

  return (
    <ul className="space-y-2">
      {items.map(([key, z]) => (
        <li key={key} className="flex items-center justify-between rounded-lg border border-surface-2 bg-surface-2 px-3 py-2">
          <span className="text-sm capitalize text-slate-400">{key.replace(/_/g, " ")}</span>
          <span className={`font-mono text-sm font-medium ${z >= 0 ? "text-macro-up" : "text-macro-down"}`}>
            {z >= 0 ? "+" : ""}
            {z.toFixed(2)}σ
          </span>
        </li>
      ))}
    </ul>
  );
}
